import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import type {
    UsersIntelligenceMapCluster,
    UsersIntelligenceMapPoint,
} from "../types";

export type UsersLiveMapViewport = {
    minLat: number;
    minLng: number;
    maxLat: number;
    maxLng: number;
    zoom: number;
};

type UsersLiveMapProps = {
    mode: "CLUSTERS" | "POINTS";
    points: UsersIntelligenceMapPoint[];
    clusters: UsersIntelligenceMapCluster[];
    isLoading?: boolean;
    errorMessage?: string | null;
    onViewportChange: (viewport: UsersLiveMapViewport) => void;
    onUserSelect?: (userId: string, role: string) => void;
};

const DEFAULT_CENTER: [number, number] = [6.5244, 3.3792];
const DEFAULT_ZOOM = 10;

const formatEnumLabel = (value: string | null) => {
    if (!value) return "—";

    return value
        .toLowerCase()
        .split("_")
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(" ");
};

const getPointTone = (role: string) => {
    if (role === "DRIVER") {
        return { fill: "#f59e0b", stroke: "#b45309" };
    }

    if (role === "RIDER") {
        return { fill: "#3b82f6", stroke: "#1d4ed8" };
    }

    return { fill: "#8b5cf6", stroke: "#6d28d9" };
};

const createClusterIcon = (cluster: UsersIntelligenceMapCluster) => {
    const size = Math.max(34, Math.min(76, 28 + cluster.totalUsers * 1.2));

    return L.divIcon({
        className: "",
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        html: `
            <div style="
                width:${size}px;
                height:${size}px;
                border-radius:9999px;
                display:flex;
                align-items:center;
                justify-content:center;
                font-size:12px;
                font-weight:700;
                color:#ffffff;
                background:rgba(14,116,144,0.92);
                border:1px solid rgba(255,255,255,0.9);
                box-shadow:0 10px 30px rgba(14,116,144,0.25);
            ">
                ${cluster.totalUsers}
            </div>
        `,
    });
};

export const UsersLiveMap = ({
    mode,
    points,
    clusters,
    isLoading = false,
    errorMessage,
    onViewportChange,
    onUserSelect,
}: UsersLiveMapProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<L.Map | null>(null);
    const overlayLayerRef = useRef<L.LayerGroup | null>(null);
    const onViewportChangeRef = useRef(onViewportChange);
    const onUserSelectRef = useRef(onUserSelect);

    useEffect(() => {
        onViewportChangeRef.current = onViewportChange;
    }, [onViewportChange]);

    useEffect(() => {
        onUserSelectRef.current = onUserSelect;
    }, [onUserSelect]);

    useEffect(() => {
        if (!containerRef.current || mapRef.current) {
            return;
        }

        const map = L.map(containerRef.current, {
            zoomControl: true,
            minZoom: 6,
            maxZoom: 18,
        });

        const overlayLayer = L.layerGroup().addTo(map);

        mapRef.current = map;
        overlayLayerRef.current = overlayLayer;

        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution:
                '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 19,
            updateWhenIdle: true,
            keepBuffer: 2,
        }).addTo(map);

        const emitViewport = () => {
            const bounds = map.getBounds();

            onViewportChangeRef.current({
                minLat: bounds.getSouth(),
                minLng: bounds.getWest(),
                maxLat: bounds.getNorth(),
                maxLng: bounds.getEast(),
                zoom: map.getZoom(),
            });
        };

        map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
        map.whenReady(emitViewport);
        map.on("moveend", emitViewport);
        map.on("zoomend", emitViewport);

        const handleResize = () => {
            map.invalidateSize();
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            map.off("moveend", emitViewport);
            map.off("zoomend", emitViewport);
            map.remove();
            mapRef.current = null;
            overlayLayerRef.current = null;
        };
    }, []);

    useEffect(() => {
        const overlayLayer = overlayLayerRef.current;

        if (!overlayLayer) {
            return;
        }

        overlayLayer.clearLayers();

        if (mode === "CLUSTERS") {
            clusters.forEach((cluster) => {
                const marker = L.marker([cluster.latitude, cluster.longitude], {
                    icon: createClusterIcon(cluster),
                    keyboard: false,
                });

                marker.bindTooltip(
                    `${cluster.totalUsers} users · ${cluster.riders} riders · ${cluster.drivers} drivers`,
                    {
                        direction: "top",
                        offset: [0, -10],
                    }
                );

                marker.addTo(overlayLayer);
            });

            return;
        }

        points.forEach((point) => {
            const tone = getPointTone(point.role);
            const marker = L.circleMarker([point.latitude, point.longitude], {
                radius: point.role === "DRIVER" ? 8 : 7,
                color: tone.stroke,
                weight: 2,
                fillColor: tone.fill,
                fillOpacity: 0.92,
            });

            marker.bindTooltip(
                `${point.firstName} ${point.lastName} · ${formatEnumLabel(point.role)}`,
                {
                    direction: "top",
                    offset: [0, -8],
                }
            );

            if (onUserSelectRef.current) {
                marker.on("click", () =>
                    onUserSelectRef.current?.(point.userId, point.role)
                );
            }

            marker.addTo(overlayLayer);
        });
    }, [clusters, mode, points]);

    const hasData = mode === "CLUSTERS" ? clusters.length > 0 : points.length > 0;

    return (
        <div className="relative h-[32rem] w-full overflow-hidden bg-slate-100 dark:bg-slate-950">
            <div ref={containerRef} className="h-full w-full" />

            {isLoading ? (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/65 backdrop-blur-[1px] dark:bg-slate-950/60">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
                </div>
            ) : null}

            {!isLoading && errorMessage ? (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/70 px-6 text-center backdrop-blur-[1px] dark:bg-slate-950/70">
                    <div>
                        <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">
                            {errorMessage}
                        </p>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                            Move the map or refresh to try again.
                        </p>
                    </div>
                </div>
            ) : null}

            {!isLoading && !errorMessage && !hasData ? (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/40 px-6 text-center backdrop-blur-[1px] dark:bg-slate-950/40">
                    <div>
                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                            No live users matched this viewport.
                        </p>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                            Try a broader area, a wider freshness window, or another role filter.
                        </p>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
